import { invoiceTypes } from "../../pages/Invoicing/InvoiceTypesMapping";
import {
  React,
  MenuItem,
  FormControl,
  Typography,
  Select,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Box,
} from "../../utils/globalImports";

interface Option {
  value: string | number;
  text: string;
}

const MAX_VISIBLE_CHIPS = 2;

interface CustomMultiSelectDropdownProps {
  selectedValues: (string | number)[];
  onChange: (selectedValues: (string | number)[]) => void;
  label: string;
  options?: Option[];
  requiredLabel?: boolean;
  required?: boolean;
  sx?: object;
  disable?: boolean;
  singleVisibleChipMode?: boolean;
  enableSelectAll?: boolean;
}

const CustomMultiSelectDropdown: React.FC<CustomMultiSelectDropdownProps> = ({
  selectedValues,
  onChange,
  label,
  options = [],
  requiredLabel = false,
  required = true,
  sx = {},
  disable,
  singleVisibleChipMode = false,
  enableSelectAll,
}) => {
  

  const allValues = options.map(o => o.value);

  const isAllSelected =
    allValues.length > 0 &&
    allValues.every(v => selectedValues.includes(v));
  const SELECT_ALL_VALUE = "__select_all__";
    const handleChange = (event: any) => {
    const { value } = event.target;

    if (!Array.isArray(value)) {
      onChange([]);
      return;
    }

    if (value.includes(SELECT_ALL_VALUE)) {
      onChange(isAllSelected ? [] : allValues);
    } else {
      onChange(value);
    }
  };


  // const handleSelectAll = () => {
  //   if (isAllSelected) {
  //     onChange([]);           // unselect all
  //   } else {
  //     onChange(allValues);    // select all
  //   }
  // };

  // Handle chip removal without opening the dropdown
  const handleDelete =
    (valueToRemove: string | number) => (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onChange(selectedValues.filter((value) => value !== valueToRemove));
    };
    

  return (
    <FormControl fullWidth sx={{ ...sx }}>
      {/* Required Label Above Dropdown */}
      {requiredLabel && (
        <Typography
          variant="subtitle1"
          sx={{ color: "#1e66ab", fontSize: 14, fontWeight: 500, ml: 1 }}
        >
          {label}
          {required && <span style={{ color: "red" }}>*</span>}
        </Typography>
      )}

      <Select
        multiple
        value={selectedValues}
        onChange={handleChange}
        disabled={disable}
        displayEmpty
        onClose={(event) => event.stopPropagation()}
        input={
          <OutlinedInput
            sx={{
              backgroundColor: "#fff",
              borderRadius: "50px",
              padding: "4px 12px", // reduce top padding to align
              height: "35px", // Match TextField height
              minHeight: "35px",
              "& .MuiOutlinedInput-input": {
                padding: "8px 0px",
                fontSize: "14px",
              },
            }}
          />
        }
        sx={{
          backgroundColor: "#fff",
          borderRadius: "50px",
          minHeight: "35px",
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#fff",
            borderRadius: "50px",
            padding: "0px", // Remove extra padding
            height: "35px", // Match TextField height
            minHeight: "35px",
          },
          "& .MuiSelect-select": {
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            padding: "4px 12px", // adjust padding to align placeholder
            fontSize: "12px",
            height: "35px", // Match TextField height
            minHeight: "35px",
            boxSizing: "border-box",
            lineHeight: 1.4, // aligns better with textfield
          },
          "& .MuiPaper-root": {
            borderRadius: "14px",
          },
          "& .MuiTypography-root": {
            paddingTop: "4px",
            fontSize: "14px",
          },
        }}
        renderValue={(selected) => {
          const validSelected = selected.filter((value) =>
            options.some((opt) => opt.value === value)
          );

          if (validSelected.length === 0) {
            return (
              <Typography sx={{ color: "#aaa", fontSize: 12 }}>
                {label}
              </Typography>
            );
          }


          const maxVisible = singleVisibleChipMode ? 1 : MAX_VISIBLE_CHIPS;

          const visibleSelections = validSelected.slice(0, maxVisible);
          const hiddenCount = validSelected.length - maxVisible;
          // const visibleSelections = validSelected.slice(0, MAX_VISIBLE_CHIPS);
          // const hiddenCount = validSelected.length - MAX_VISIBLE_CHIPS;

          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {visibleSelections.map((value) => {
                const option = options.find((opt) => opt.value === value);
                return (
                  <Chip
                    key={value}
                    label={option?.text}
                    size="small"
                    onDelete={disable ? undefined : handleDelete(value)}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{
                      borderRadius: "12px",
                      backgroundColor: "#1e66ab",
                      color: "#fff",
                      fontSize: 12,
                      "& .MuiChip-deleteIcon": {
                        color: "#fff",
                      },
                    }}
                  />
                );
              })}
              {hiddenCount > 0 && (
                <Chip
                  label={`+${hiddenCount} more`}
                  size="small"
                  sx={{
                    borderRadius: 12,
                    backgroundColor: "#e0e0e0",
                    color: "#333",
                    fontSize: 12,
                  }}
                />
              )}
            </Box>
          );
        }}
        MenuProps={{
          disableAutoFocusItem: true,
          PaperProps: {
            sx: {
              borderRadius: "12px",
              maxHeight: 200, // Set maximum height for the dropdown menu
            },
          },
        }}
      >
        {enableSelectAll && options.length > 0 && (
          <MenuItem value={SELECT_ALL_VALUE}>
            <Checkbox
              checked={isAllSelected}
              indeterminate={
                selectedValues.length > 0 &&
                selectedValues.length < allValues.length
              }
            />
            <ListItemText
              primary={isAllSelected ? "Unselect All" : "Select All"}
            />
          </MenuItem>
        )}

        {options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            sx={{ borderRadius: "10px", margin: "4px" }}
          >
            <Checkbox
              checked={selectedValues.includes(option.value)}
              sx={{
                color: "rgba(150, 193, 33, 0.75) !important",
                "&.Mui-checked": {
                  color: "rgba(150, 193, 33, 0.75) !important",
                },
                "& .MuiSvgIcon-root": {
                  fontSize: "18px",
                  backgroundColor: "rgba(169, 201, 81, 0.12)",
                  borderRadius: "4px",
                },
              }}
            />
            <ListItemText primary={option.text} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default CustomMultiSelectDropdown;
